import { useState, useEffect } from 'react';
import { useSprintStore, Sprint, SprintStatus } from '../../../store/sprintStore';
import { useProjectStore } from '../../../store/projectStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Search, Target, Play, CheckCircle2, Pencil, Trash2 } from 'lucide-react';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import toast from 'react-hot-toast';

const STATUS_OPTIONS: SprintStatus[] = ['planning', 'active', 'completed', 'cancelled'];

const today = () => new Date().toISOString().slice(0, 10);
const inTwoWeeks = () => new Date(Date.now() + 14 * 86400_000).toISOString().slice(0, 10);
const emptyForm = () => ({
  projectId: '',
  name: '',
  goal: '',
  status: 'planning' as SprintStatus,
  startDate: today(),
  endDate: inTwoWeeks(),
});

const statusVariant = (s: SprintStatus): 'success' | 'primary' | 'secondary' | 'warning' =>
  s === 'active' ? 'primary' : s === 'completed' ? 'success' : s === 'cancelled' ? 'secondary' : 'warning';

export function SprintManagement() {
  const { sprints, isLoading, fetchSprints, createSprint, updateSprint, deleteSprint } = useSprintStore();
  const { projects, fetchProjects } = useProjectStore();
  const [search, setSearch] = useState('');
  const [isFormOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchSprints();
    fetchProjects();
  }, []);

  const projectName = (id: string) => projects.find((p) => p.id === id)?.name || '—';

  const filtered = sprints.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    projectName(s.projectId).toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm(), projectId: projects[0]?.id || '' });
    setErrors({});
    setFormOpen(true);
  };

  const openEdit = (s: Sprint) => {
    setEditingId(s.id);
    setForm({
      projectId: s.projectId,
      name: s.name,
      goal: s.goal || '',
      status: s.status,
      startDate: (s.startDate || '').slice(0, 10) || today(),
      endDate: (s.endDate || '').slice(0, 10) || inTwoWeeks(),
    });
    setErrors({});
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    const next: Record<string, string> = {};
    if (!form.projectId) next.projectId = 'Project is required';
    if (!form.name.trim()) next.name = 'Name is required';
    if (!form.startDate) next.startDate = 'Start date is required';
    if (!form.endDate) next.endDate = 'End date is required';
    if (form.startDate && form.endDate && form.endDate < form.startDate) next.endDate = 'End must be on or after start';
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      const payload = {
        projectId: form.projectId,
        name: form.name.trim(),
        goal: form.goal.trim() || undefined,
        status: form.status,
        startDate: form.startDate,
        endDate: form.endDate,
      };
      if (editingId) {
        await updateSprint(editingId, payload);
        toast.success('Sprint updated');
      } else {
        await createSprint(payload);
        toast.success('Sprint created');
      }
      setFormOpen(false);
    } catch {
      toast.error(editingId ? 'Failed to update sprint' : 'Failed to create sprint');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSprint(deleteTarget.id);
      toast.success('Sprint deleted');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete sprint');
    } finally {
      setDeleting(false);
    }
  };

  const activeCount = sprints.filter((s) => s.status === 'active').length;
  const completedCount = sprints.filter((s) => s.status === 'completed').length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Sprints</h1>
          <p className="text-muted-foreground">Plan, run, and close out your agile sprints.</p>
        </div>
        <Button variant="primary" onClick={openCreate} disabled={projects.length === 0}>
          <Plus className="w-4 h-4 mr-2" />
          New Sprint
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
              <Target className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Sprints</p>
              <h3 className="text-foreground font-display text-metric">{sprints.length}</h3>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
              <Play className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Active</p>
              <h3 className="text-foreground font-display text-metric">{activeCount}</h3>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Completed</p>
              <h3 className="text-foreground font-display text-metric">{completedCount}</h3>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <h3 className="text-foreground font-display text-section-title">Sprint List</h3>
            <div className="relative w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search sprints..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                <tr>
                  <th className="px-6 py-3">Sprint</th>
                  <th className="px-6 py-3">Project</th>
                  <th className="px-6 py-3">Dates</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">
                      No sprints found.
                    </td>
                  </tr>
                ) : filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-secondary/20 transition-colors">
                    <td className="px-6 py-4 font-medium text-foreground">
                      {s.name}
                      {s.goal && <div className="text-xs text-muted-foreground font-normal line-clamp-1">{s.goal}</div>}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">{projectName(s.projectId)}</td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {s.startDate ? new Date(s.startDate).toLocaleDateString() : '—'}
                      {' → '}
                      {s.endDate ? new Date(s.endDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={statusVariant(s.status)}>{s.status.toUpperCase()}</Badge>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon-sm" aria-label="Edit sprint"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => openEdit(s)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon-sm" aria-label="Delete sprint"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleteTarget({ id: s.id, name: s.name })}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Create / edit modal */}
      <Dialog open={isFormOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Sprint' : 'New Sprint'}</DialogTitle>
            <DialogDescription>{editingId ? 'Update the sprint details.' : 'Plan a new sprint for a project.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Project</label>
              <select
                value={form.projectId}
                onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                disabled={!!editingId}
              >
                <option value="">Select a project…</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {errors.projectId && <p className="text-xs text-destructive">{errors.projectId}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Name</label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Sprint 12 — Checkout revamp" invalid={!!errors.name} />
              {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Goal</label>
              <Textarea rows={2} value={form.goal} onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))}
                placeholder="What should this sprint achieve?" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Start date</label>
                <Input type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} invalid={!!errors.startDate} />
                {errors.startDate && <p className="text-xs text-destructive">{errors.startDate}</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">End date</label>
                <Input type="date" value={form.endDate} onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))} invalid={!!errors.endDate} />
                {errors.endDate && <p className="text-xs text-destructive">{errors.endDate}</p>}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as SprintStatus }))}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} isLoading={saving}>{editingId ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete sprint</DialogTitle>
            <DialogDescription>
              Delete <span className="font-medium text-foreground">{deleteTarget?.name}</span>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} isLoading={deleting}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
